// The property page's at-a-glance section: the Quick CMA in one line, the
// facts, the strategy in two sentences, and the photos with the hero pick
// and the ones the photo review says could improve. Same cards, same
// palette as the rest of the page; nothing here is a new surface for
// selling — the picker and the files live in Personalize and Share.
import { useState } from "react";
import styled from "styled-components";

import { API_URL } from "../../constants";
import { formatDate } from "../DashboardPage/propertyFeed";
import {
  factsGlance,
  heroPhoto,
  money,
  moneyShort,
  packageGlance,
  photoNotes,
  priceGlance,
  recommendedOrder,
  strategyGlance,
} from "./glance";
import { Card, CardLabel, HintText, QuietAction } from "./styled";

const PriceHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Verdict = styled.span`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${(/** @type {{ $tone?: string }} */ p) => (p.$tone === "high" ? "#92400e" : p.$tone === "low" ? "#0b605d" : "#166534")};
`;

const Range = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  margin: 4px 0 6px;

  strong {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: #1a1a1a;
    font-variant-numeric: tabular-nums;
  }

  span {
    font-size: 0.85rem;
    color: #6b7280;
  }

  span b {
    color: #1a1a1a;
    font-weight: 600;
  }
`;

const Scale = styled.div`
  position: relative;
  height: 40px;
  margin: 2px 0 6px;
`;

const Rail = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 16px;
  height: 4px;
  border-radius: 2px;
  background: #e5e7eb;
`;

const Band = styled.div`
  position: absolute;
  top: 13px;
  height: 10px;
  border-radius: 5px;
  background: #0e7e7a;
  opacity: 0.85;
`;

const Anchor = styled.div`
  position: absolute;
  top: 10px;
  width: 3px;
  height: 16px;
  border-radius: 2px;
  background: #0b605d;
  transform: translateX(-50%);
`;

const ListTick = styled.div`
  position: absolute;
  top: 5px;
  transform: translateX(-50%);
  display: grid;
  justify-items: center;
  gap: 3px;

  i {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #d97706;
    border: 2px solid #ffffff;
    box-shadow: 0 0 0 1px #d97706;
  }

  em {
    font-style: normal;
    font-size: 0.7rem;
    font-weight: 700;
    color: #92400e;
    white-space: nowrap;
  }
`;

const Ends = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 28px;
  display: flex;
  justify-content: space-between;
  font-size: 0.72rem;
  color: #9ca3af;
  font-variant-numeric: tabular-nums;
`;

const Why = styled.p`
  font-size: 0.9rem;
  color: #374151;
  margin: 0;
`;

const Facts = styled.div`
  display: flex;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  padding: 12px 0;
  margin: 16px 0;

  div {
    flex: 1;
    min-width: 0;
  }

  strong {
    display: block;
    font-size: 1.05rem;
    color: #1a1a1a;
    font-variant-numeric: tabular-nums;
  }

  span {
    font-size: 0.7rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #9ca3af;
  }
`;

const Rows = styled.div`
  display: grid;
  gap: 10px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 84px 1fr;
  gap: 12px;
  align-items: baseline;
  font-size: 0.9rem;
  color: #374151;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 2px;
  }
`;

const RowKey = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
`;

const RowValue = styled.span`
  b {
    color: #1a1a1a;
    font-weight: 600;
  }

  a {
    color: #0e7e7a;
    font-weight: 600;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const Preview = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  aspect-ratio: 16 / 10;
  max-height: 380px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const Chips = styled.div`
  position: absolute;
  left: 12px;
  top: 12px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;

  span {
    font-size: 0.75rem;
    font-weight: 600;
    color: #92400e;
    background: rgba(254, 243, 199, 0.94);
    border: 1px solid #d97706;
    border-radius: 999px;
    padding: 3px 9px;
  }
`;

const HeroTag = styled.span`
  position: absolute;
  right: 12px;
  top: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #0b605d;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 999px;
  padding: 4px 10px;
`;

const Caption = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  font-size: 0.82rem;
  color: #ffffff;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.55));

  b {
    font-weight: 600;
  }

  span {
    font-variant-numeric: tabular-nums;
    opacity: 0.9;
  }
`;

const Strip = styled.ul`
  list-style: none;
  margin: 0;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 10px 2px 6px;
  scrollbar-width: thin;
`;

const Thumb = styled.button`
  flex: 0 0 auto;
  width: 72px;
  aspect-ratio: 4 / 3;
  border: 2px solid ${(/** @type {{ $current?: boolean }} */ p) => (p.$current ? "#0e7e7a" : "transparent")};
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  background: #f3f4f6;
  cursor: pointer;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &:focus-visible {
    outline: 2px solid #0e7e7a;
    outline-offset: 2px;
  }
`;

const ThumbStar = styled.span`
  position: absolute;
  left: 5px;
  top: 3px;
  font-size: 0.7rem;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
`;

const ThumbDot = styled.span`
  position: absolute;
  right: 5px;
  top: 5px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d97706;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
`;

const Improve = styled.div`
  margin-top: 14px;
  border-top: 1px solid #f0f0f0;
  padding-top: 14px;

  h3 {
    font-size: 1rem;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 10px;
  }
`;

const ImproveList = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
`;

const ImproveItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 0.85rem;
  color: #374151;

  img {
    width: 56px;
    aspect-ratio: 4 / 3;
    border-radius: 6px;
    object-fit: cover;
    flex-shrink: 0;
    background: #f3f4f6;
  }

  b {
    display: block;
    color: #1a1a1a;
    font-weight: 600;
  }

  span {
    color: #6b7280;
    font-size: 0.8rem;
  }
`;

/**
 * @param {{
 *   uuid: string,
 *   doc: Record<string, any>,
 *   latest: import("../DashboardPage/propertyFeed").ReportSummary,
 * }} props
 */
const PropertyGlance = ({ uuid, doc, latest }) => {
  const content = doc.content || {};
  const manifest = /** @type {Record<string, string>} */ (doc.photo_manifest || {});
  const order = recommendedOrder(content, manifest);
  const hero = heroPhoto(content, manifest);
  const notes = photoNotes(content);
  const [current, setCurrent] = useState(0);
  const price = priceGlance(content);
  const facts = factsGlance(content);
  const strategy = strategyGlance(content);
  const ready = packageGlance(doc);
  const improvable = order.filter((n) => notes[n]?.findings.length);
  const editUrl = `/reports/${uuid}/edit`;
  const token = doc.photo_access_token || "";
  const src = (/** @type {string} */ name) =>
    `${API_URL}/computations/${uuid}/photo/${encodeURIComponent(name)}/${token ? `?pt=${encodeURIComponent(token)}` : ""}`;
  const shown = order[Math.min(current, Math.max(order.length - 1, 0))];
  const shownNote = shown ? notes[shown] : null;
  const sharedLine = latest.shared_at ? `shared ${formatDate(latest.shared_at)}` : "not shared yet";

  const pct = (/** @type {number} */ v) =>
    price && price.spanLow != null && price.spanHigh != null && price.spanHigh > price.spanLow
      ? `${(((v - price.spanLow) / (price.spanHigh - price.spanLow)) * 100).toFixed(1)}%`
      : null;
  const scale =
    price && price.low != null && price.high != null && pct(price.low) && pct(price.high) ? (
      <Scale
        role="img"
        aria-label={`Suggested range ${money(price.low)} to ${money(price.high)}${price.list != null ? `; list price ${money(price.list)}` : ""}`}
      >
        <Rail />
        <Band
          style={{
            left: pct(price.low) || 0,
            width: `${(((price.high - price.low) / ((price.spanHigh || 0) - (price.spanLow || 0))) * 100).toFixed(1)}%`,
          }}
        />
        {price.anchor != null && (
          <Anchor
            style={{ left: pct(price.anchor) || 0 }}
            title={`Anchor ${money(price.anchor)}`}
          />
        )}
        {price.list != null && (
          <ListTick style={{ left: pct(price.list) || 0 }}>
            <i />
            <em>List {moneyShort(price.list)}</em>
          </ListTick>
        )}
        <Ends>
          <span>
            {price.hasComps ? "Comps from " : ""}
            {moneyShort(/** @type {number} */ (price.spanLow))}
          </span>
          <span>
            {price.hasComps ? "to " : ""}
            {moneyShort(/** @type {number} */ (price.spanHigh))}
          </span>
        </Ends>
      </Scale>
    ) : null;

  return (
    <>
      {(price || facts.length > 0 || strategy || ready.length > 0) && (
        <Card data-testid="property-glance">
          <CardLabel>At a glance</CardLabel>
          {price && (
            <>
              <PriceHead>
                <RowKey>Quick CMA</RowKey>
                {price.verdict && <Verdict $tone={price.tone}>{price.verdict}</Verdict>}
              </PriceHead>
              {price.low != null && price.high != null && (
                <Range>
                  <strong>
                    {moneyShort(price.low)}–{moneyShort(price.high)}
                  </strong>
                  <span>
                    suggested
                    {price.anchor != null && (
                      <>
                        {" "}
                        · anchor <b>{money(price.anchor)}</b>
                      </>
                    )}
                  </span>
                </Range>
              )}
              {scale}
              {price.why && <Why>{price.why}</Why>}
            </>
          )}
          {facts.length > 0 && (
            <Facts>
              {facts.map((f) => (
                <div key={f.label}>
                  <strong>{f.value}</strong>
                  <span>{f.label}</span>
                </div>
              ))}
            </Facts>
          )}
          <Rows>
            {strategy && (
              <Row>
                <RowKey>Strategy</RowKey>
                <RowValue>
                  {strategy.buyer && <b>{strategy.buyer}</b>}
                  {strategy.buyer && strategy.opportunity && <br />}
                  {strategy.opportunity}
                </RowValue>
              </Row>
            )}
            {order.length > 0 && (
              <Row>
                <RowKey>Photos</RowKey>
                <RowValue>
                  <b>{order.length} photos</b>
                  {hero && " · hero picked"}
                  {improvable.length > 0 && (
                    <>
                      {" · "}
                      <a href="#property-photos">{improvable.length} could be improved</a>
                    </>
                  )}
                </RowValue>
              </Row>
            )}
            <Row>
              <RowKey>Package</RowKey>
              <RowValue>
                {ready.length > 0 ? (
                  <>
                    <b>{ready.join(", ")}</b> ready · {sharedLine}
                  </>
                ) : (
                  <>Web share · {sharedLine}</>
                )}
              </RowValue>
            </Row>
          </Rows>
        </Card>
      )}

      {order.length > 0 && shown && (
        <Card id="property-photos" data-testid="property-photos">
          <CardLabel>Photos</CardLabel>
          <Preview>
            <img src={src(shown)} alt={shownNote?.label || "Listing photo"} />
            {shownNote && shownNote.findings.length > 0 && (
              <Chips aria-label="Could be improved">
                {shownNote.findings.map((f) => (
                  <span key={f}>{f}</span>
                ))}
              </Chips>
            )}
            {shown === hero && <HeroTag>Hero pick</HeroTag>}
            <Caption>
              <b>{shownNote?.label || ""}</b>
              <span>
                {order.indexOf(shown) + 1} of {order.length} in the recommended order
              </span>
            </Caption>
          </Preview>
          <Strip aria-label="Photos in the recommended order">
            {order.map((name, i) => (
              <li key={name}>
                <Thumb
                  type="button"
                  $current={i === current}
                  aria-current={i === current ? "true" : undefined}
                  aria-label={`${notes[name]?.label || "Photo"} ${i + 1}${name === hero ? ", hero pick" : ""}${notes[name]?.findings.length ? ", could be improved" : ""}`}
                  onClick={() => setCurrent(i)}
                >
                  <img src={src(name)} alt="" loading="lazy" />
                  {name === hero && <ThumbStar aria-hidden="true">★</ThumbStar>}
                  {notes[name]?.findings.length > 0 && <ThumbDot aria-hidden="true" />}
                </Thumb>
              </li>
            ))}
          </Strip>
          <HintText>
            ★ hero pick · <span style={{ color: "#d97706" }}>●</span> could be improved
          </HintText>
          {improvable.length > 0 && (
            <Improve>
              <h3>
                {improvable.length} {improvable.length === 1 ? "photo" : "photos"} could be improved
              </h3>
              <ImproveList>
                {improvable.map((name) => (
                  <ImproveItem key={name}>
                    <img src={src(name)} alt="" loading="lazy" />
                    <div>
                      <b>{notes[name].label || "Photo"}</b>
                      <span>{notes[name].findings.join(" · ")}</span>
                    </div>
                  </ImproveItem>
                ))}
              </ImproveList>
              <div style={{ marginTop: 14 }}>
                <QuietAction href={`${editUrl}#add-to-listing`}>
                  Choose photos to touch up
                </QuietAction>
              </div>
            </Improve>
          )}
        </Card>
      )}
    </>
  );
};

export default PropertyGlance;
